import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import axios from "../axios-orders";
import { CategoryResponse, MaterialType } from "../types";
import { queryKeys } from "./queryKeys";

const onGetCategoriesByType = async (type: MaterialType) => {
  const { data } = await axios.get(`/materialCategory/type/${type}`);
  return data;
};

export const useCategoriesByType = (pnType: MaterialType) =>
  useQuery({
    queryKey: [pnType],
    queryFn: () => onGetCategoriesByType(pnType),
    enabled: !!pnType,
  });

const onGetCategoriesById = async (categoryId: string) => {
  const { data } = await axios.get(`/materialCategory/parent/id/${categoryId}`);
  return data;
};

export const useCategoriesById = (queryKey: string, type: string) =>
  useQuery({
    queryKey: [queryKey, type],
    queryFn: () => onGetCategoriesById(type),
    enabled: !!type,
  });

const onSearchMaterialCategoryByName = async (name: string) => {
  const { data } = await axios.get(`/materialCategory/material/${name}`);
  return data;
};

export const useSearchMaterialCategoryByName = (
  debouncedSearchTerm: string,
  langEs: boolean
) =>
  useQuery({
    queryKey: [queryKeys.MATERIAL_CATEGORY_BY_NAME, debouncedSearchTerm],
    queryFn: () => {
      if (langEs) {
        return onSearchMaterialCategoryByName(debouncedSearchTerm as string);
      }
      return onSearchMaterialCategoryByName(`/en/${debouncedSearchTerm}`);
    },
    enabled: !!debouncedSearchTerm,
  });

const onGetAvailableCountries = async () => {
  const { data } = await axios.get(`/country/material`);
  return data;
};

export const useAvailableCountries = () =>
  useQuery({
    queryKey: [queryKeys.AVAILABLE_COUNTRIES],
    queryFn: onGetAvailableCountries,
  });

const onGetCategories = async (categoryIds: string[]) => {
  const { data } = await axios.get(
    `materialCategory/parent/ids?ids=${categoryIds.join(",")}`
  );
  return data;
};

export const useCategories = (
  queryKey: string,
  categories: CategoryResponse[]
) =>
  useQuery({
    queryKey: [queryKey, categories],
    queryFn: () => {
      const cats = categories.map((item: any) => item.id);
      return onGetCategories(cats);
    },
    enabled: categories.length > 0,
  });

export interface FamilyOption {
  value: string;
  label: string;
}

/**
 * Every active material category (the three type lists the analytics filter bar also merges),
 * as sorted `{value: id, label}` options named in the UI language. For a "family" `<Select>`.
 */
export const useFamilyOptions = (): FamilyOption[] => {
  const { i18n } = useTranslation();
  const { data: indirect } = useCategoriesByType(MaterialType.INDIRECT_GOODS);
  const { data: direct } = useCategoriesByType(MaterialType.DIRECT_GOODS);
  const { data: services } = useCategoriesByType(MaterialType.SERVICES);
  const isEnglish = (i18n?.language ?? "").startsWith("en");

  return useMemo(() => {
    const families = new Map<string, FamilyOption>();
    [indirect, direct, services].forEach((list) =>
      ((list ?? []) as CategoryResponse[]).forEach((c) =>
        families.set(c.id, {
          value: c.id,
          label: (isEnglish && c.nameEn) || c.name,
        })
      )
    );
    return Array.from(families.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [indirect, direct, services, isEnglish]);
};
